/**
 * DENET — ürün rayı
 * Ok düğmeleri, kaydırma durumu ve masaüstünde sürükleyerek kaydırma.
 * Dokunmatik cihazlarda tarayıcının kendi kaydırması kullanılır.
 */

import { DenetElement, define, rafThrottle, media } from '@denet/core';

class DnRail extends DenetElement {
  mounted() {
    this.track = this.ref('track');
    if (!this.track) return;

    const nav = this.closest('section')?.querySelector('[data-ref="nav"]');
    this.prev = nav?.querySelector('[data-rail="prev"]') ?? null;
    this.next = nav?.querySelector('[data-rail="next"]') ?? null;

    this.prev?.addEventListener('click', () => this.#scroll(-1), { signal: this.signal });
    this.next?.addEventListener('click', () => this.#scroll(1), { signal: this.signal });

    const update = rafThrottle(() => this.#updateState());
    this.track.addEventListener('scroll', update, { passive: true, signal: this.signal });
    window.addEventListener('resize', update, { passive: true, signal: this.signal });
    this.#updateState();

    if (media.pointerFine) this.#initDrag();
  }

  /** @param {number} direction */
  #scroll(direction) {
    if (!this.track) return;
    // Bir "sayfa" kadar değil, görünen genişliğin %80'i kadar: kullanıcı
    // nerede kaldığını kaybetmez.
    const step = this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  #updateState() {
    if (!this.track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;

    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;
    this.style.setProperty('--dn-rail-fade-end', atEnd ? '0' : '1');
  }

  #initDrag() {
    const track = /** @type {HTMLElement} */ (this.track);
    let pointerId = /** @type {number | null} */ (null);
    let startX = 0;
    let startScroll = 0;
    let moved = 0;

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      moved = 0;
    }, { signal: this.signal });

    track.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId) return;
      const delta = event.clientX - startX;
      moved = Math.abs(delta);
      if (moved > 4) {
        track.classList.add('is-dragging');
        track.setPointerCapture(pointerId);
      }
      track.scrollLeft = startScroll - delta;
    }, { signal: this.signal });

    const end = () => {
      if (pointerId === null) return;
      if (track.hasPointerCapture(pointerId)) track.releasePointerCapture(pointerId);
      pointerId = null;
      track.classList.remove('is-dragging');
    };

    for (const evt of ['pointerup', 'pointercancel', 'pointerleave']) {
      track.addEventListener(evt, end, { signal: this.signal });
    }

    // Sürükleyerek biten hareket bağlantıyı açmamalı.
    track.addEventListener('click', (event) => {
      if (moved > 6) { event.preventDefault(); event.stopPropagation(); }
    }, { capture: true, signal: this.signal });
  }
}

define('dn-rail', DnRail);
